import { useState } from "react";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";
import { toast } from "@/components/feedback/toast";
import { adminFetch } from "@/api";

export interface UserActionsBarProps {
  userId: string;
  emailVerified: boolean;
  authProvider: string;
  isSuspended?: boolean;
  isDeleted?: boolean;
}

async function postJson(
  path: string,
  body?: Record<string, unknown>,
): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  const res = await adminFetch(path, {
    method: "POST",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, data };
}

export function UserActionsBar({
  userId,
  emailVerified,
  authProvider,
  isSuspended,
  isDeleted,
}: UserActionsBarProps) {
  const [busy, setBusy] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const onForceLogout = async () => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(`/api/users/${userId}/force-logout`);
      if (!ok) {
        toast.error("Force logout failed");
        return;
      }
      const count = Number(data.revokedCount ?? 0);
      toast.success(
        `Force-logout sent — ${count} refresh token${count === 1 ? "" : "s"} revoked`,
      );
    } finally {
      setBusy(false);
    }
  };

  const onResendVerification = async () => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(
        `/api/users/${userId}/resend-verification`,
      );
      if (!ok) {
        toast.error(String(data.error ?? "Resend failed"));
        return;
      }
      toast.success(String(data.message ?? "Verification email sent"));
    } finally {
      setBusy(false);
    }
  };

  const onSendPasswordReset = async () => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(
        `/api/users/${userId}/send-password-reset`,
      );
      if (!ok) {
        toast.error(String(data.error ?? "Reset email failed"));
        return;
      }
      toast.success(String(data.message ?? "Password reset email sent"));
    } finally {
      setBusy(false);
    }
  };

  const submitSuspend = async (reason: string) => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(`/api/users/${userId}/suspend`, {
        reason: reason.trim() || undefined,
      });
      if (!ok) {
        toast.error(String(data.error ?? "Suspend failed"));
        return;
      }
      toast.success(
        `Suspended · ${data.revokedRefreshTokens} session${
          data.revokedRefreshTokens === 1 ? "" : "s"
        } revoked`,
      );
      setShowSuspend(false);
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const onUnsuspend = async () => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(`/api/users/${userId}/unsuspend`);
      if (!ok) {
        toast.error(String(data.error ?? "Unsuspend failed"));
        return;
      }
      toast.success("User unsuspended");
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async (anonymize: boolean) => {
    setBusy(true);
    try {
      const { ok, data } = await postJson(`/api/users/${userId}/delete`, {
        anonymize,
      });
      if (!ok) {
        toast.error(String(data.error ?? "Delete failed"));
        return;
      }
      toast.success(
        `Deleted${anonymize ? " + anonymized" : ""} · ${data.revokedRefreshTokens} session${
          data.revokedRefreshTokens === 1 ? "" : "s"
        } revoked`,
      );
      setShowDelete(false);
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || isDeleted;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {!isDeleted ? (
          <button
            type="button"
            onClick={onForceLogout}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-b from-fc-brand to-emerald-600 px-3 py-1.5 text-[12.5px] font-semibold text-emerald-950 hover:brightness-105 disabled:opacity-60"
          >
            Force logout
          </button>
        ) : null}

        {!isDeleted && !emailVerified ? (
          <button
            type="button"
            onClick={onResendVerification}
            disabled={disabled}
            className="rounded-md border border-fc-border bg-fc-overlay px-3 py-1.5 text-[12.5px] text-fc-text hover:border-fc-brand disabled:opacity-60"
          >
            Resend verification
          </button>
        ) : null}

        {!isDeleted && authProvider === "email" ? (
          <button
            type="button"
            onClick={onSendPasswordReset}
            disabled={disabled}
            className="rounded-md border border-fc-border bg-fc-overlay px-3 py-1.5 text-[12.5px] text-fc-text hover:border-fc-brand disabled:opacity-60"
          >
            Send password reset
          </button>
        ) : null}

        {!isDeleted && !isSuspended ? (
          <button
            type="button"
            onClick={() => setShowSuspend(true)}
            disabled={disabled}
            className="rounded-md border border-fc-warn/40 bg-fc-warn/10 px-3 py-1.5 text-[12.5px] font-semibold text-fc-warn hover:brightness-110 disabled:opacity-60"
          >
            Suspend
          </button>
        ) : null}

        {isSuspended && !isDeleted ? (
          <button
            type="button"
            onClick={onUnsuspend}
            disabled={disabled}
            className="rounded-md border border-fc-brand/40 bg-fc-brand/10 px-3 py-1.5 text-[12.5px] font-semibold text-fc-brand hover:brightness-110 disabled:opacity-60"
          >
            Unsuspend
          </button>
        ) : null}

        {!isDeleted ? (
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            disabled={disabled}
            className="rounded-md border border-fc-bad/40 bg-fc-bad/10 px-3 py-1.5 text-[12.5px] font-semibold text-fc-bad hover:brightness-110 disabled:opacity-60"
          >
            Delete
          </button>
        ) : (
          <span className="text-[12px] text-fc-text-dim">
            Account deleted — actions disabled
          </span>
        )}
      </div>

      {showSuspend && (
        <SuspendDialog
          busy={busy}
          onCancel={() => setShowSuspend(false)}
          onConfirm={submitSuspend}
        />
      )}
      {showDelete && (
        <DeleteDialog
          busy={busy}
          onCancel={() => setShowDelete(false)}
          onConfirm={submitDelete}
        />
      )}
    </>
  );
}

function DialogShell({
  title,
  icon,
  tone,
  children,
  onCancel,
}: {
  title: string;
  icon: React.ReactNode;
  tone: "warn" | "bad";
  children: React.ReactNode;
  onCancel: () => void;
}) {
  const chip =
    tone === "bad"
      ? "bg-red-500/10 border-red-500/20 text-red-300"
      : "bg-amber-500/10 border-amber-500/20 text-amber-300";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#0b0b0d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-xl border flex items-center justify-center ${chip}`}
            >
              {icon}
            </div>
            <h2 className="text-[16px] font-semibold text-white">{title}</h2>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg text-zinc-500 hover:text-white hover:bg-white/[0.04] flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SuspendDialog({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <DialogShell
      title="Suspend user"
      icon={<ShieldAlert className="w-4 h-4" />}
      tone="warn"
      onCancel={onCancel}
    >
      <div className="space-y-2">
        <p className="text-[13px] text-zinc-400">
          User cannot log in and every active session is revoked. Reversible via{" "}
          <span className="text-zinc-300">Unsuspend</span>.
        </p>
        <label className="block text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
          Reason (optional, shown in audit trail)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Repeated spam reports"
          className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/30 resize-none"
          autoFocus
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[13px] text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-all disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(reason)}
          disabled={busy}
          className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black text-[13px] font-semibold hover:bg-amber-400 disabled:opacity-40 transition-all"
        >
          {busy ? "Suspending…" : "Suspend"}
        </button>
      </div>
    </DialogShell>
  );
}

function DeleteDialog({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (anonymize: boolean) => void;
}) {
  const [anonymize, setAnonymize] = useState(false);
  return (
    <DialogShell
      title="Delete account"
      icon={<AlertTriangle className="w-4 h-4" />}
      tone="bad"
      onCancel={onCancel}
    >
      <div className="space-y-3">
        <p className="text-[13px] text-zinc-400">
          Soft-delete this account. Login is blocked immediately and every
          active session is revoked. Expense history is preserved.
        </p>
        <label className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06] cursor-pointer hover:border-red-500/30 transition-all">
          <input
            type="checkbox"
            checked={anonymize}
            onChange={(e) => setAnonymize(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-red-500"
          />
          <span className="text-[12px]">
            <span className="block text-zinc-200 font-medium">
              Also anonymize PII
            </span>
            <span className="block text-zinc-500">
              Blanks email, name, avatar. Cannot be reversed. Leave unchecked
              to keep them for support lookups.
            </span>
          </span>
        </label>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[13px] text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-all disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(anonymize)}
          disabled={busy}
          className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:bg-red-400 disabled:opacity-40 transition-all"
        >
          {busy ? "Deleting…" : anonymize ? "Delete + anonymize" : "Delete"}
        </button>
      </div>
    </DialogShell>
  );
}
